import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  CreditCard, 
  FileText, 
  Calendar,
  DollarSign,
  Loader2,
  Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicePaymentPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("client_token");
    if (!token) {
      navigate("/portal");
      return;
    }
    fetchInvoice(token);
  }, [invoiceId, navigate]);

  const fetchInvoice = async (token) => {
    try {
      const response = await axios.get(`${API}/client/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoice(response.data);
    } catch (error) {
      console.error("Error fetching invoice:", error);
      toast.error("Invoice not found");
      navigate("/portal/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStripePayment = async () => {
    setIsProcessing(true);
    const token = localStorage.getItem("client_token");

    try {
      const response = await axios.post(
        `${API}/payments/stripe/checkout`,
        {
          invoice_id: invoiceId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Payment failed");
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: "bg-green-500/20 text-green-400 border-green-500/30",
      unpaid: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      overdue: "bg-red-500/20 text-red-400 border-red-500/30",
      cancelled: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    };
    return (
      <span className={`px-4 py-2 text-sm uppercase tracking-wider border ${styles[status]}`}>
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="invoice-payment-page">
      {/* Header */}
      <header className="bg-[#121212] border-b border-[#2A2A2A] py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/portal/dashboard" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white">
            <ArrowLeft size={18} />
            Back to Dashboard
          </Link>
          <img 
            src="https://customer-assets.emergentagent.com/job_1eeea6a6-945d-4052-ae21-011aad17a3a1/artifacts/qm929xya_fulllogo_transparent_nobuffer.png" 
            alt="Astute Builders" 
            className="h-10"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Invoice Details */}
        <div className="bg-[#121212] border border-[#2A2A2A] mb-8">
          <div className="p-6 border-b border-[#2A2A2A] flex items-center justify-between">
            <div>
              <h1 className="font-['Oswald'] text-2xl text-white mb-1">
                Invoice {invoice.invoice_number}
              </h1>
              <p className="text-[#A1A1AA]">{invoice.description}</p>
            </div>
            {getStatusBadge(invoice.status)}
          </div>

          <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-1">
                <DollarSign size={14} />
                Amount
              </div>
              <p className="font-['Oswald'] text-3xl text-white">
                ${invoice.amount.toLocaleString()}
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-1">
                <Calendar size={14} />
                Due Date
              </div>
              <p className="text-white text-lg">
                {new Date(invoice.due_date).toLocaleDateString()}
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-1">
                <FileText size={14} />
                Invoice Date
              </div>
              <p className="text-white text-lg">
                {new Date(invoice.created_at).toLocaleDateString()}
              </p>
            </div>
            {invoice.pdf_filename && (
              <div>
                <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-1">
                  <Download size={14} />
                  Attachment
                </div>
                <button className="text-[#7ED4C6] hover:underline text-sm">
                  Download PDF
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Payment Section */}
        {invoice.status !== "paid" ? (
          <div className="bg-[#121212] border border-[#2A2A2A] p-8">
            <h2 className="font-['Oswald'] text-xl text-white mb-6">Select Payment Method</h2>
            
            <div className="space-y-4">
              {/* Stripe Payment */}
              <button
                onClick={handleStripePayment}
                disabled={isProcessing}
                className="w-full bg-[#1E1E1E] border border-[#2A2A2A] p-6 flex items-center justify-between hover:border-[#7ED4C6]/50 transition-colors group disabled:opacity-50"
                data-testid="stripe-payment-btn"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#7ED4C6] flex items-center justify-center">
                    <CreditCard size={24} className="text-[#0A0A0A]" />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold">Credit Card / ACH</p>
                    <p className="text-[#A1A1AA] text-sm">Pay securely via Stripe</p>
                  </div>
                </div>
                {isProcessing ? (
                  <Loader2 size={24} className="text-[#7ED4C6] animate-spin" />
                ) : (
                  <span className="text-[#7ED4C6] font-['Oswald'] uppercase tracking-wider group-hover:translate-x-2 transition-transform">
                    Pay ${invoice.amount.toLocaleString()}
                  </span>
                )}
              </button>

              {/* PayPal - Coming Soon */}
              <div className="w-full bg-[#1E1E1E] border border-[#2A2A2A] p-6 flex items-center justify-between opacity-50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#0070BA] flex items-center justify-center">
                    <span className="text-white font-bold text-lg">P</span>
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold">PayPal</p>
                    <p className="text-[#A1A1AA] text-sm">Coming soon</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-[#A1A1AA] text-sm mt-6 text-center">
              All payments are secure and encrypted. You will receive a receipt via email.
            </p>
          </div>
        ) : (
          <div className="bg-green-500/10 border border-green-500/30 p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard size={32} className="text-green-400" />
            </div>
            <h2 className="font-['Oswald'] text-2xl text-white mb-2">Invoice Paid</h2>
            <p className="text-[#A1A1AA]">
              This invoice was paid on {invoice.payment_date ? new Date(invoice.payment_date).toLocaleDateString() : 'N/A'}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
